import { Injectable } from '@angular/core';
import { Patient } from '../models/patient.model';
import { PathologyType } from '../models/pathology.enum';
import { Drug } from '@shared/models/drug.model';
import { Criteria } from '@shared/models/criteria.model';

@Injectable()
export class RemediesService {
  private population = 4010;
  private timeRef = 1735689600;
  private week4 = 604800 * 4;
  private week12 = 604800 * 12;

  public processRemediesCriterias = (
    data: Array<Patient>,
    drugs: { [key: string]: Drug },
    pathologies: { [key: string]: PathologyType }
  ): Array<Criteria> => {
    const remediesCriterias: Array<Criteria> = [];

    // prepare patien prescriptions
    const patients = data
      .filter(p => p.age > 64)
      .map(p => {
        return {
          ...p,
          codes: p.prescriptions
            .map(pres => drugs[pres.name.toUpperCase().trim()].atc)
            .filter((psyco, p, array) => array.indexOf(psyco) === p)
        };
      });

    const D7 = {
      name: 'D-7',
      description: `l’utilisation concomitante de 2 et plus psychotropes de la même classe pharmacologique >2 N05B ou >2 N06A ou >2 N05A`,
      patients: []
    } as Criteria;
    patients.forEach(patient => {
      if (
        patient.codes.filter(code => code.includes('N05B')).length >= 2 ||
        patient.codes.filter(code => code.includes('N06A')).length >= 2 ||
        patient.codes.filter(code => code.includes('N05A')).length >= 2
      ) {
        D7.patients.push(patient);
      }
    });
    remediesCriterias.push(D7);

    const BR1BR6 = {
      name: 'B/R-1 et B/R-6',
      description: `présence de hydroxyzine (N05BB01) ou l’amitriptyline (N06AA09), la clomipramine (N06AA04), la doxépine (N06AA12), et l’imipramine (N06AA02)`,
      patients: []
    } as Criteria;
    patients.forEach(patient => {
      if (
        patient.codes.find(code => code.includes('N05BB01')) ||
        patient.codes.find(code => code.includes('N06AA09')) ||
        patient.codes.find(code => code.includes('N06AA04')) ||
        patient.codes.find(code => code.includes('N06AA12')) ||
        patient.codes.find(code => code.includes('N06AA02'))
      ) {
        BR1BR6.patients.push(patient);
      }
    });
    remediesCriterias.push(BR1BR6);

    const BR8 = {
      name: 'B/R-8',
      description: `présence de phénothiaziniques tels que : la chlorpromazine (N05AA01), la cyamemazine (N05AA06), le flupenthixol (N05AF01), la lévomépromazine (N05AA02) et le zuclopenthixol (N05AF05)`,
      patients: []
    } as Criteria;
    patients.forEach(patient => {
      if (
        patient.codes.find(code => code.includes('N05AA01')) ||
        patient.codes.find(code => code.includes('N05AA06')) ||
        patient.codes.find(code => code.includes('N05AF01')) ||
        patient.codes.find(code => code.includes('N05AA02')) ||
        patient.codes.find(code => code.includes('N05AF05'))
      ) {
        BR8.patients.push(patient);
      }
    });
    remediesCriterias.push(BR8);

    const BR11 = {
      name: 'B/R-11',
      description: `présence de l’ALIMEMAZINE (R06AD01)`,
      patients: []
    } as Criteria;
    patients.forEach(patient => {
      if (patient.codes.find(code => code.includes('R06AD01'))) {
        BR11.patients.push(patient);
      }
    });
    remediesCriterias.push(BR11);

    const DU1 = {
      name: 'DU-1',
      description: `présence de BZD > 12 semaines si usage à visée anxiolytique : Alprazolam (N05BA12)  , Bromazépam (N05BA08) Clorazépate (N05BA05), Clotiazépam (N05BA21), Diazépam (N05BA01), Lorazépam (N05BA06), Oxazépam (N05BA04), Prazépam (N05BA11)`,
      patients: []
    } as Criteria;
    patients.forEach(patient => {
      if (
        patient.prescriptions
          .filter(pres => pres.start + this.week12 < this.timeRef)
          .map(pres => drugs[pres.name.toUpperCase().trim()].atc)
          .find(
            code =>
              code.includes('N05BA12') ||
              code.includes('N05BA08') ||
              code.includes('N05BA05') ||
              code.includes('N05BA21') ||
              code.includes('N05BA01') ||
              code.includes('N05BA06') ||
              code.includes('N05BA04') ||
              code.includes('N05BA11')
          )
      ) {
        DU1.patients.push(patient);
      }
    });
    remediesCriterias.push(DU1);

    const DU2 = {
      name: 'DU-2',
      description: `présence de Benzodiazépines et molécules apparentées > 4 semaines si usage à visée hypnotique : Estazolam (N05CD04), Lormétazépam (N05CD06) Zolpidem (N05CF02), Zopiclone (N05CF01)`,
      patients: []
    } as Criteria;
    patients.forEach(patient => {
      if (
        patient.prescriptions
          .filter(pres => pres.start + this.week4 < this.timeRef)
          .map(pres => drugs[pres.name.toUpperCase().trim()].atc)
          .find(
            code =>
              code.includes('N05CD04') ||
              code.includes('N05CD06') ||
              code.includes('N05CF02') ||
              code.includes('N05CF01')
          )
      ) {
        DU2.patients.push(patient);
      }
    });
    remediesCriterias.push(DU2);

    const CC1 = {
      name: 'CC-1',
      description: `antécédent HTO et antipsychotiques conventionnels et atypiques (N05A) `,
      patients: []
    } as Criteria;
    patients.forEach(patient => {
      if (
        patient.codes.find(code => code.includes('N05A')) &&
        patient.antecedents.map(atcd => atcd.toLocaleUpperCase()).includes('HTO')
      ) {
        CC1.patients.push(patient);
      }
    });
    remediesCriterias.push(CC1);

    const CC5 = {
      name: 'CC-5',
      description: `antécédent constipation avec anticholinergiques (Hydroxyzine N05BB01, Amitriptyline N06AA09, Clomipramine N06AA04, Doxépine N06AA12, Cyamémazine N05AA06, Lévomépromazine N05AA02, Zuclopenthixol N05AF05, Alimémazine R06AD01 `,
      patients: []
    } as Criteria;
    patients.forEach(patient => {
      if (
        (patient.codes.find(code => code.includes('N05BB01')) ||
          patient.codes.find(code => code.includes('N06AA09')) ||
          patient.codes.find(code => code.includes('N06AA04')) ||
          patient.codes.find(code => code.includes('N06AA12')) ||
          patient.codes.find(code => code.includes('N05AA06')) ||
          patient.codes.find(code => code.includes('N05AA02')) ||
          patient.codes.find(code => code.includes('N05AF05')) ||
          patient.codes.find(code => code.includes('R06AD01'))) &&
        patient.antecedents.map(atcd => atcd.toLocaleUpperCase()).find(atcd => atcd.includes('CONSTIPATION'))
      ) {
        CC5.patients.push(patient);
      }
    });
    remediesCriterias.push(CC5);

    const CC8 = {
      name: 'CC-8',
      description: `antécédent démence avec BZD et apparentés (N05B ou N05C)`,
      patients: []
    } as Criteria;
    patients.forEach(patient => {
      if (
        (patient.codes.find(code => code.includes('N05B')) || patient.codes.find(code => code.includes('N05C'))) &&
        patient.antecedents
          .map(atcd => atcd.toLocaleUpperCase())
          .find(atcd => atcd.includes('DEMENCE') || atcd.includes('DÉMENCE'))
      ) {
        CC8.patients.push(patient);
      }
    });
    remediesCriterias.push(CC8);

    const CC9 = {
      name: 'CC-9',
      description: `antécédent démence avec anticholinergiques (Hydroxyzine N05BB01, Amitriptyline N06AA09, Clomipramine N06AA04, Doxépine N06AA12, Cyamémazine N05AA06, Lévomépromazine N05AA02 ;  Zuclopenthixol N05AF05, Alimémazine R06AD01)`,
      patients: []
    } as Criteria;
    patients.forEach(patient => {
      if (
        (patient.codes.find(code => code.includes('N05BB01')) ||
          patient.codes.find(code => code.includes('N06AA09')) ||
          patient.codes.find(code => code.includes('N06AA04')) ||
          patient.codes.find(code => code.includes('N06AA12')) ||
          patient.codes.find(code => code.includes('N05AA06')) ||
          patient.codes.find(code => code.includes('N05AA02')) ||
          patient.codes.find(code => code.includes('N05AF05')) ||
          patient.codes.find(code => code.includes('R06AD01'))) &&
        patient.antecedents
          .map(atcd => atcd.toLocaleUpperCase())
          .find(atcd => atcd.includes('DEMENCE') || atcd.includes('DÉMENCE'))
      ) {
        CC9.patients.push(patient);
      }
    });
    remediesCriterias.push(CC9);

    const CC10 = {
      name: 'CC-10',
      description: `antécédent démence avec neuroleptiques conventionnel et atypiques (N05A)`,
      patients: []
    } as Criteria;
    patients.forEach(patient => {
      if (
        patient.codes.find(code => code.includes('N05A')) &&
        patient.antecedents
          .map(atcd => atcd.toLocaleUpperCase())
          .find(atcd => atcd.includes('DEMENCE') || atcd.includes('DÉMENCE'))
      ) {
        CC10.patients.push(patient);
      }
    });
    remediesCriterias.push(CC10);

    const CC12 = {
      name: 'CC-12',
      description: `antécédent HBP ou RAU avec anticholinergiques (Hydroxyzine N05BB01, Amitriptyline N06AA09, Clomipramine N06AA04, Doxépine N06AA12, Cyamémazine N05AA06, Lévomépromazine N05AA02 ;  Zuclopenthixol N05AF05, Alimémazine R06AD01)`,
      patients: []
    } as Criteria;
    patients.forEach(patient => {
      if (
        (patient.codes.find(code => code.includes('N05BB01')) ||
          patient.codes.find(code => code.includes('N06AA09')) ||
          patient.codes.find(code => code.includes('N06AA04')) ||
          patient.codes.find(code => code.includes('N06AA12')) ||
          patient.codes.find(code => code.includes('N05AA06')) ||
          patient.codes.find(code => code.includes('N05AA02')) ||
          patient.codes.find(code => code.includes('N05AF05')) ||
          patient.codes.find(code => code.includes('R06AD01'))) &&
        patient.antecedents
          .map(atcd => atcd.toLocaleUpperCase())
          .find(atcd => atcd.includes('HBP') || atcd.includes('RAU'))
      ) {
        CC12.patients.push(patient);
      }
    });
    remediesCriterias.push(CC12);

    const I15 = {
      name: 'I-15',
      description: `Utilisation concomitante de 3 et plus médicaments dépresseurs du système nerveux central (N05B ou N05A ou N06A) `,
      patients: []
    } as Criteria;
    patients.forEach(patient => {
      if (
        patient.codes.filter(code => code.includes('N05B') || code.includes('N05A') || code.includes('N06A')).length >=
        3
      ) {
        I15.patients.push(patient);
      }
    });
    remediesCriterias.push(I15);

    const I16 = {
      name: 'I-16',
      description: `Utilisation concomitante de 2 et plus médicaments avec des propriétés sérotoninergiques (N06A)`,
      patients: []
    } as Criteria;
    patients.forEach(patient => {
      if (patient.codes.filter(code => code.includes('N06A')).length >= 2) {
        I16.patients.push(patient);
      }
    });
    remediesCriterias.push(I16);

    const I18 = {
      name: 'I-18',
      description: `Utilisation concomitante de 2 et plus médicaments aux propriétés anticholinergiques (Hydroxyzine N05BB01, Amitriptyline N06AA09, Clomipramine N06AA04, Doxépine N06AA12, Cyamémazine N05AA06, Lévomépromazine N05AA02 ;  Zuclopenthixol N05AF05, Alimémazine R06AD01)`,
      patients: []
    } as Criteria;
    patients.forEach(patient => {
      if (
        patient.codes.filter(
          code =>
            code.includes('N05BB01') ||
            code.includes('N06AA09') ||
            code.includes('N06AA04') ||
            code.includes('N06AA12') ||
            code.includes('N05AA06') ||
            code.includes('N05AA02') ||
            code.includes('N05AF05') ||
            code.includes('R06AD01')
        ).length >= 2
      ) {
        I18.patients.push(patient);
      }
    });
    remediesCriterias.push(I18);

    // console.log('Final analysis');
    const ordered = remediesCriterias; //.sort((a, b) => b.patients.length - a.patients.length);

    return ordered;
  };
}
